'use client';

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { PageHero } from '@/components/shared/PageHero';
import { InsightCard } from '@/components/shared/InsightCard';
import { Link } from '@/components/shared/Link';
import { format } from 'date-fns';
import { MediaImg } from '@/components/shared/MediaImg';
import { stripHtml } from '@/lib/utils';
import { getInsightsList } from '@/lib/actions/insights';

export default function InsightsPage() {
  const [searchParams] = useSearchParams();
  const industrySlug = searchParams.get('industry') || undefined;
  const [articles, setArticles] = useState<any[]>([]);
  const [footprint, setFootprint] = useState<any>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    void getInsightsList(industrySlug).then((data) => {
      setArticles(data.articles);
      setFootprint(data.footprint);
      setReady(true);
    });
  }, [industrySlug]);

  if (!ready) return null;

  let title = 'Insights';
  let subtitle = 'Explore our perspectives on strategy, people, and performance.';
  let metaTitle = 'Insights & Perspectives';
  let metaDescription = 'Explore our latest research and experience in strategy, people, and performance.';

  if (industrySlug && footprint) {
    title = footprint.title;
    subtitle = `Perspectives and insights tailored for the ${footprint.title} sector.`;
    metaTitle = `${footprint.title} Insights`;
    metaDescription = `Perspectives and insights tailored for the ${footprint.title} sector.`;
  }

  return (
    <>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
      </Helmet>
      <PageHero
        title={title}
        subtitle={subtitle}
        breadcrumbs={[
          { label: 'Home', href: '/' },
          { label: 'Insights' }
        ]}
      />

      <section className="py-16 md:py-24 px-5 md:px-[40px] bg-white">
        <div className="max-w-[1170px] mx-auto">

          {articles.length > 0 ? (
            <div className="space-y-20">
              {!industrySlug && (
                <div className="group relative overflow-hidden rounded-3xl border border-light shadow-2xl shadow-navy/5 bg-white">
                  <div className="grid grid-cols-1 lg:grid-cols-2">
                    <div className="aspect-[16/10] lg:aspect-auto relative overflow-hidden">
                      <MediaImg
                        src={articles[0].coverImage || '/images/simisi/page13_img1.png'}
                        alt={articles[0].title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                    </div>
                    <div className="p-8 md:p-12 lg:p-16 flex flex-col justify-center">
                      <span className="text-[10px] font-bold text-primary tracking-[0.2em] uppercase mb-4 block">
                        Featured Insight • {format(new Date(articles[0].publishDate), 'MMM dd, yyyy')}
                      </span>
                      <h2 className="text-3xl md:text-4xl font-bold text-navy mb-6 font-heading leading-tight group-hover:text-primary transition-colors">
                        {articles[0].title}
                      </h2>
                      <p className="text-lg text-gray-500 font-sans font-light leading-relaxed mb-8 line-clamp-3">
                        {stripHtml(articles[0].content || '')}
                      </p>
                      <Link
                        href={`/insights/${articles[0].slug}`}
                        className="inline-flex items-center text-[13px] font-bold text-navy uppercase tracking-widest hover:text-primary transition-colors"
                      >
                        Read Article →
                      </Link>
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
                {(industrySlug ? articles : articles.slice(1)).map((item) => (
                  <InsightCard
                    key={item.id}
                    slug={item.slug}
                    title={item.title}
                    description={item.summary || item.content || ''}
                    category={item.serviceCategory?.name}
                    date={format(new Date(item.publishDate), 'MMM dd, yyyy')}
                    image={item.coverImage || ''}
                    footprints={item.footprints}
                    showDescription={false}
                  />
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-32 border-2 border-dashed border-light rounded-3xl">
              <p className="text-xl text-gray-400 font-sans italic">No articles found for this industry yet.</p>
              <Link href="/insights" className="text-primary font-bold mt-4 inline-block hover:underline">
                View all insights
              </Link>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
